import * as bootstrap from 'bootstrap';
import { DefaultCreditCardDelimiter, formatDate, registerCursorTracker } from 'cleave-zen';
import * as models from 'smg/customer-api/models';
import * as html from 'smg/html';

import { State } from '../state';
import { NumberToPaymentOptionTypeID } from '../util';

// InitCCInputs wires the still-native credit-card affordances — the MM/YY
// expiration formatter and the CVC tooltip — inside the given element scope.
// Scope defaults to the payment body when omitted. The PAN and CVC now render
// inside BlockChyp iframes (PCI SAQ A), so number grouping and card-type
// detection are handled by BlockChyp: the brand icon is driven by the Mount's
// onValidation callback via SyncCardIcon (see ./forms/credit).
export function InitCCInputs(state: State, $el?: HTMLElement): void {
    const $scope = $el || state.dom.$paymentBody;

    $scope?.querySelectorAll<HTMLInputElement>('.cc-exp-input').forEach($exp => {
        registerCursorTracker({ input: $exp, delimiter: DefaultCreditCardDelimiter });
        $exp.addEventListener('input', () => {
            $exp.value = formatDate($exp.value, { datePattern: ['m', 'y'] });
        });
    });

    $scope?.querySelectorAll<HTMLElement>('.cc-cvc-hint').forEach($hint => {
        const t = new bootstrap.Tooltip($hint, {
            trigger: 'manual',
            placement: 'top',
            title: '3- or 4-digit security code on the back of your card',
            html: true,
            offset: [0, 32],
            customClass: 'cc-cvc-tooltip',
        });
        $hint.addEventListener('pointerenter', () => t.show());
        $hint.addEventListener('pointerleave', () => t.hide());
    });
}

// SyncCardIcon toggles the .cc-icon brand glyphs from a BlockChyp card type
// (reported by the iframe's validation event). The PAN digits live in the
// cross-origin iframe, so brand detection comes from BlockChyp rather than a
// local regex over the number. Case-insensitive substring match — BlockChyp's
// raw cardType casing is not a stable contract.
export function SyncCardIcon($scope: Element | null | undefined, cardType: string): void {
    const normalized = cardType.toLowerCase();
    let icon = 'generic';
    if (normalized.includes('amex') || normalized.includes('american')) {
        icon = 'amex';
    } else if (normalized.includes('discover')) {
        icon = 'discover';
    } else if (normalized.includes('master')) {
        icon = 'mastercard';
    } else if (normalized.includes('visa')) {
        icon = 'visa';
    }
    $scope?.querySelectorAll<HTMLElement>('.cc-icon').forEach($icon => {
        $icon.classList.toggle('d-none', !$icon.classList.contains(`cc-icon-${icon}`));
    });
}

// InitPaymentTypeSelect populates the payment-type dropdown with every
// payment method and re-prompts for a new payment option when the user
// picks a different type.
export function InitPaymentTypeSelect(state: State, typeID: models.PaymentOptionTypeID, $el?: HTMLElement): void {
    const $scope = $el || state.dom.$paymentBody;
    const $poSelectParent = $scope?.querySelector<HTMLDivElement>('#po-type-select') as HTMLDivElement;
    html.IDsToClasses($poSelectParent);

    const $poSelect = $poSelectParent?.querySelector<HTMLSelectElement>('.po-type') as HTMLSelectElement;
    // Bank Account is intentionally omitted: its picker radio and form
    // template are commented out in payment-templates.html, and prompts.ts
    // bails on BANK routing. Keep this list in sync with the picker.
    const options: [models.PaymentOptionTypeID, string][] = [
        [models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT, 'Credit/Debit Card'],
        [models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL, 'Paypal'],
        [models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO, 'Purchase Order'],
        [models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK, 'Check'],
        [models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE, 'Wire Transfer'],
    ];
    options.forEach(([value, label]) => {
        $poSelect.appendChild(html.FromString(`<option value="${value}">${label}</option>`));
    });

    $poSelect.value = typeID.toString();
    $poSelect.addEventListener('change', () => {
        const newType = NumberToPaymentOptionTypeID(parseInt($poSelect.value, 10));
        if (newType) {
            state.promptNewPaymentOptionByType(newType, $scope ?? undefined);
        }
    });
}
